import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, Clock, Phone, BookOpen, ChevronRight } from 'lucide-react';
import { api } from '../api';
import { useFetch } from '../hooks/useFetch';
import { useToast } from '../context/ToastContext';
import BookingCalendar from '../components/BookingCalendar';
import PageHero from '../components/PageHero';

const TIME_SLOTS = ['08:00','09:00','10:00','11:00','13:00','14:00','15:00','16:00','17:00'];

export default function Teorilektioner() {
  const toast = useToast();
  const { data: packages } = useFetch(() => api.getTheoryPackages());
  const pkgs = packages || [{id:'theory-1',name:'1 teorilektion',sessions:1,pris:400},{id:'theory-5',name:'5 teorilektioner',sessions:5,pris:1800},{id:'theory-10',name:'10 teorilektioner',sessions:10,pris:3200}];

  const [selectedPkg, setSelectedPkg] = useState(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [form, setForm] = useState({ name:'', email:'', phone:'' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const pkg = pkgs.find(p => p.id === selectedPkg);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPkg || !date || !time) { toast('Välj paket, datum och tid.', 'error'); return; }
    setSubmitting(true);
    try {
      await api.createTheoryBooking({ packageId: selectedPkg, date, time, ...form });
      setDone(true);
      toast('Teorilektion bokad!', 'success');
    } catch (err) { toast(err.message, 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <PageHero title="Teorilektioner" desc="Genomgång av trafikregler, vägmärken och allt du behöver veta för att klara teoriprovet." breadcrumbs={[{label:'Teorilektioner'}]}/>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-[1fr_360px] gap-10">
          <div>
            {/* Info cards */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-5">
              <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-3 flex items-center gap-2"><FileText size={18} className="text-red-600"/> Om Teorilektioner</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Under en teorilektion på Alfa Trafikskola i Kista går vi igenom trafikregler, vägmärken, körkortsteorin och repeterar med övningsfrågor. Perfekt komplement till övningskörning.</p>
              <div className="grid grid-cols-2 gap-3">
                {['Alla trafikregler','Vägmärken & skyltar','Körkortsteori','Övningsfrågor','Strategier för provet','Kistamiljöns vägar'].map(t=>(
                  <div key={t} className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={12} className="text-red-500 shrink-0"/>{t}</div>
                ))}
              </div>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-2xl p-5 mb-5 flex items-start gap-4">
              <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center shrink-0"><BookOpen size={17} className="text-white"/></div>
              <div>
                <h3 className="font-bold text-red-800 mb-1">Prova gratis teoriprov online!</h3>
                <p className="text-red-600 text-sm mb-3">65 frågor, precis som det riktiga provet. Identifiera dina svaga områden.</p>
                <Link to="/teoriprov" className="btn-red-sm inline-flex">Starta Teoriprov <ChevronRight size={12}/></Link>
              </div>
            </div>

            {/* Packages */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-5">
              <h2 className="font-display font-black text-xl text-gray-900 uppercase mb-4">Välj Paket</h2>
              <div className="grid grid-cols-3 gap-3">
                {pkgs.map(p => (
                  <button key={p.id} type="button" onClick={() => setSelectedPkg(p.id)}
                    className={`relative text-center p-4 rounded-xl border-2 transition-all ${selectedPkg===p.id ? 'border-red-600 bg-red-50' : 'border-gray-100 hover:border-red-200'}`}>
                    <div className="font-display font-black text-3xl text-red-600">{p.sessions}</div>
                    <div className="text-xs text-gray-400 mb-2">lektion{p.sessions>1?'er':''}</div>
                    <div className="font-bold text-gray-900">{p.pris} kr</div>
                    {p.sessions > 1 && <div className="text-xs text-gray-400">{Math.round(p.pris/p.sessions)} kr/st</div>}
                    {selectedPkg===p.id && <div className="absolute top-2 right-2 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center text-white text-[10px] font-black">✓</div>}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar & Time */}
            {selectedPkg && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h2 className="font-display font-black text-xl text-gray-900 uppercase mb-4">Välj Tid</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Datum</div>
                    <BookingCalendar selected={date} onChange={setDate}/>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Tid</div>
                    {date ? (
                      <div className="grid grid-cols-3 gap-2">
                        {TIME_SLOTS.map(t => (
                          <button key={t} type="button" onClick={() => setTime(t)}
                            className={`py-2.5 rounded-lg text-xs font-mono font-semibold border transition-colors ${time===t ? 'bg-red-600 text-white border-red-600' : 'border-gray-200 text-gray-600 hover:border-red-300'}`}>
                            {t}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32 border-2 border-dashed border-gray-100 rounded-xl text-sm text-gray-400">Välj datum</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Booking sidebar */}
          <div className="lg:sticky lg:top-28 space-y-4">
            {done ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
                <div className="text-4xl mb-3">✅</div>
                <h3 className="font-display font-black text-2xl text-gray-900 uppercase mb-2">Bokad!</h3>
                <p className="text-gray-500 text-sm">Vi ses på teorilektionen!</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-4">Dina Uppgifter</h3>
                <div className="space-y-3 mb-4">
                  {[['name','Namn','text','Förnamn Efternamn'],['email','E-post','email','din@email.se'],['phone','Telefon','tel','073-000 00 00']].map(([k,l,t,ph])=>(
                    <div key={k}>
                      <label className="label">{l} *</label>
                      <input type={t} required placeholder={ph} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} className="input-field"/>
                    </div>
                  ))}
                </div>
                {pkg && date && time && (
                  <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm">
                    <div className="flex justify-between py-1"><span className="text-gray-500">Paket</span><strong>{pkg.sessions} lektion{pkg.sessions>1?'er':''}</strong></div>
                    <div className="flex justify-between py-1"><span className="text-gray-500">Datum</span><strong>{date}</strong></div>
                    <div className="flex justify-between py-1"><span className="text-gray-500">Tid</span><strong>{time}</strong></div>
                    <div className="flex justify-between py-1"><span className="text-gray-500">Pris</span><strong className="text-red-600">{pkg.pris} kr</strong></div>
                  </div>
                )}
                <button type="submit" disabled={submitting||!selectedPkg||!date||!time} className="btn-red w-full justify-center disabled:opacity-40">
                  {submitting ? 'Bokar...' : <><CheckCircle size={14}/> Boka Teorilektion</>}
                </button>
              </form>
            )}
            <div className="bg-gray-900 rounded-2xl p-5">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Ring och boka</h3>
              <a href="tel:+46737547405" className="btn-red w-full justify-center mb-2 text-sm"><Phone size={13}/> Abubakar: 073-754 74 05</a>
              <a href="tel:+46704815743" className="btn-red w-full justify-center text-sm"><Phone size={13}/> Ahmed: 070-481 57 43</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
